import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  type FacetedFilterOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  ScheduledFundMovementDescription,
  StatusUI,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { formatLocalDateComponents } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import {
  RiBuilding2Line,
  RiCalendarLine,
  RiFilter3Line,
  RiPriceTagLine,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import { scheduledFundMovementStatusValues } from "~/gql/client/enumValues";
import type {
  AdminScheduledFundMovementRowFragment,
  ScheduledFundMovementFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/scheduled-fund-movements")({
  head: () => opsHead("Scheduled Fund Movements"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();

  graphql(`
    fragment AdminScheduledFundMovementRow on ScheduledFundMovement {
      id
      shortRef
      status
      amount {
        amount
        currency
      }
      requestedProcessingDate
      isFx
      isCrossEntity
      organisation {
        ...OrganisationCardFields
      }
      ...ScheduledFundMovementDescriptionFields
    }
  `);

  const scheduledFundMovementsDocument = graphql(`
    query AdminScheduledFundMovements($after: String, $first: Int!, $filter: ScheduledFundMovementFilter!) {
      scheduledFundMovements(after: $after, first: $first, filter: $filter) {
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCount
        scheduledFundMovements {
          ...AdminScheduledFundMovementRow
        }
      }
    }
  `);

  const [filterState, setFilterState] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.array(z.string()).optional().nullable(),
      startDateInclusive: z.date().optional().nullable(),
      endDateExclusive: z.date().optional().nullable(),
      status: z
        .array(z.enum(scheduledFundMovementStatusValues))
        .optional()
        .nullable(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 20,
    filter: filterState,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    scheduledFundMovementsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const rows = data?.scheduledFundMovements?.scheduledFundMovements ?? [];
  const nextCursor = data?.scheduledFundMovements?.pageInfo.hasNextPage
    ? (data.scheduledFundMovements.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminScheduledFundMovementsPage {
        organisations {
          id
          name
        }
      }
    `),
  );

  const organisationOptions: FacetedFilterOption[] = (
    pageData?.organisations?.slice() ?? []
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      value: o.id,
      searchValues: [o.name],
      label: <div>{o.name}</div>,
    }));

  return (
    <AdminPageContainer testId="admin-scheduled-fund-movements">
      <AdminPageHeader
        title="Scheduled Fund Movements"
        subtitle="Transfers and withdrawals scheduled for future processing."
      />
      <FilterBar
        filterValue={filterState}
        onFilterChange={setFilterState}
        organisationOptions={organisationOptions}
      />
      <DataTable
        data={rows}
        columns={columns}
        isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
        manualSorting={false}
        manualPagination={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        cellClassName="text-xs p-2"
        linkTo={(row) => `/scheduled-fund-movements/${row.original.id}`}
        initialColumnVisibility={{
          id: false,
        }}
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!rows.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<
  AdminScheduledFundMovementRowFragment & { id: string }
>[] = [
  {
    header: "ID",
    accessorKey: "id",
    cell: () => null,
  },
  {
    id: "shortRef",
    accessorFn: (row) => row.shortRef,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Ref" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-tertiary">#{row.original.shortRef}</span>
    ),
  },
  {
    id: "requestedProcessingDate",
    accessorFn: (row) => row.requestedProcessingDate,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Processing date" />
    ),
    cell: ({ row }) => {
      if (!row.original.requestedProcessingDate) {
        return <span className="text-tertiary">—</span>;
      }
      const { shortDate } = formatLocalDateComponents(
        new Date(row.original.requestedProcessingDate),
      );
      return <div>{shortDate}</div>;
    },
  },
  {
    id: "organisation",
    accessorFn: (row) => row.organisation?.name,
    header: "Organisation",
    cell: ({ row }) =>
      row.original.organisation ? (
        <OrganisationCard
          organisation={row.original.organisation}
          labelClassName="text-xs"
        />
      ) : null,
  },
  {
    id: "description",
    accessorFn: () => undefined,
    header: "Description",
    cell: ({ row }) => (
      <div className="flex flex-row flex-wrap items-center gap-2">
        <ScheduledFundMovementDescription
          scheduledFundMovement={row.original}
        />
      </div>
    ),
  },
  {
    id: "amount",
    accessorFn: () => undefined,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Amount"
        align="right"
        className="flex flex-row justify-end pr-2"
      />
    ),
    cell: ({ row }) => {
      if (!row.original.amount) {
        return null;
      }
      const { amount, currency } = row.original.amount;
      return (
        <div className="flex w-full items-center justify-end">
          <CurrencyAmountUI amount={amount} currency={currency} />
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
  },
];

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: ScheduledFundMovementFilter;
  onFilterChange: (_: ScheduledFundMovementFilter) => void;
  organisationOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");

  const filterCount =
    0 +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.status?.length ?? 0) > 0 ? 1 : 0) +
    (filterValue?.startDateInclusive || filterValue?.endDateExclusive ? 1 : 0);

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      organisationIds: undefined,
      status: undefined,
    });
  };

  const statusOptions = scheduledFundMovementStatusValues.map((s) => ({
    value: s,
    label: <span>{s}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.status?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("org")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.status ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    status: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "org" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues.filter((id) => id !== null)
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.status ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("org");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
